import React, { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import "./dashboard.css";
import { authFetch } from "../../../../Authentication/authService";

const COLORS = ["#F59E0B", "#22C55E"];

const Dashboard = () => {
  const [stats, setStats] = useState({});
  const [userTasks, setUserTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [questionInsights, setQuestionInsights] = useState({
    top_questions_asked: [],
    lowest_relevance_scores: [],
  });
  const ventureRole = localStorage.getItem("venture_role");
  const [description, setDescription] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);

  const UserName = localStorage.getItem("venture_email");
  const OrgName = "personal-workspace";

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("user");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [techStaff, setTechStaff] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, usersRes, insightsRes, filesRes, techStaffRes] =
        await Promise.all([
          authFetch(
            "https://api-discloseai.essolutions.ai/api/v1/workspace/fetch-all-stats-wrkspc"
          ),
          authFetch(
            "https://api-discloseai.essolutions.ai/api/v1/workspace/fetch-task-per-user-wrkspc"
          ),
          authFetch(
            "https://api-discloseai.essolutions.ai/api/v1/workspace/question-insights-wrkspc"
          ),
          authFetch(
            "https://api-discloseai.essolutions.ai/api/v1/workspace/fetch-filenames-for-wrkspc",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                org_id: 8,
              }),
            }
          ),
          authFetch(
            "https://api-discloseai.essolutions.ai/api/v1/workspace/fetch-techstaff"
          ),
        ]);

      const techStaffData = await techStaffRes.json();

      setTechStaff(techStaffData || []);

      const statsData = await statsRes.json();
      const usersData = await usersRes.json();
      const insightsData = await insightsRes.json();
      const filesData = await filesRes.json();

      setStats(statsData || {});
      setUserTasks(usersData || []);
      setQuestionInsights(
        insightsData || {
          top_questions_asked: [],
          lowest_relevance_scores: [],
        }
      );
      setUploadedFiles(filesData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) {
      alert("Please enter an email.");
      return;
    }

    try {
      setInviteLoading(true);

      const response = await authFetch(
        "https://api-discloseai.essolutions.ai/api/v1/workspace/invite-user-wrkspc",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            invites: [
              {
                email: inviteEmail,
                role: inviteRole,
              },
            ],
          }),
        }
      );

      const data = await response.json();

      alert(data.message || "Invitation sent successfully.");

      setInviteEmail("");
      setInviteRole("user");
    } catch (err) {
      console.error(err);
      alert("Unable to send invitation.");
    } finally {
      setInviteLoading(false);
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading Dashboard...</div>;
  }

  const complaintData = [
    {
      name: "In Progress",
      value: stats.in_progress || 0,
    },
    {
      name: "Completed",
      value: stats.completed || 0,
    },
  ];

  const handleFileChange = (e) => {
    setSelectedFiles(Array.from(e.target.files));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please select at least one file.");
      return;
    }

    try {
      setUploadLoading(true);

      const formData = new FormData();

      formData.append("description", description);
      formData.append("username", UserName);
      formData.append("project_name", OrgName);

      selectedFiles.forEach((file) => {
        formData.append("file", file);
      });

      const response = await authFetch(
        "https://api-discloseai.essolutions.ai/api/v1/admin/Upload-doc-by-admin",
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      alert(result.message || "Documents uploaded successfully.");

      setDescription("");
      setSelectedFiles([]);
    } catch (err) {
      console.error(err);
      alert("Upload failed.");
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Workspace Dashboard</h2>
        <p>Overview of workspace activities</p>
      </div>

      <div className="team-card">
        <div className="team-header">
          <h3>Technical Staff</h3>
          <span>{techStaff.length} Members</span>
        </div>

        <div className="team-grid">
          {techStaff.map((member, index) => (
            <div key={index} className="team-member">
              <div className="team-avatar">
                {member.charAt(0).toUpperCase()}
              </div>

              <span>{member}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="stats-grid">
        <div className="dashboard-card">
          <h4>Total Complaints</h4>
          <h2>{stats.total_complaints}</h2>
        </div>

        <div className="dashboard-card">
          <h4>In Progress</h4>
          <h2>{stats.in_progress}</h2>
        </div>

        <div className="dashboard-card">
          <h4>Completed</h4>
          <h2>{stats.completed}</h2>
        </div>

        <div className="dashboard-card">
          <h4>Chatbot Queries</h4>
          <h2>{stats.total_chatbot_queries}</h2>
        </div>

        <div className="dashboard-card">
          <h4>Knowledge Base</h4>
          <h2>{stats.knowledgebase_items_count}</h2>
        </div>
      </div>

      {ventureRole === "admin" && (
        <div className="admin-card">
          <div className="admin-header">
            <h3>Invite Team Member</h3>
            <p>Invite a new member to your workspace.</p>
          </div>

          <div className="invite-form">
            <div className="invite-field">
              <label>Email Address</label>

              <input
                type="email"
                placeholder="Enter email address"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>

            <div className="invite-field">
              <label>Role</label>

              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            <button
              className="invite-btn"
              onClick={handleInvite}
              disabled={inviteLoading}
            >
              {inviteLoading ? "Sending..." : "Invite User"}
            </button>
          </div>
        </div>
      )}

      {ventureRole === "admin" && (
        <div className="admin-card">
          <div className="admin-header">
            <h3>Upload Knowledge Documents</h3>
            <p>Upload one or more documents to the workspace knowledge base.</p>
          </div>

          <div className="upload-form">
            <div className="invite-field">
              <label>Description</label>

              <textarea
                rows="4"
                placeholder="Enter document description..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="invite-field">
              <label>Select Documents</label>

              <input type="file" multiple onChange={handleFileChange} />
            </div>

            {selectedFiles.length > 0 && (
              <div className="selected-files">
                <h5>Selected Files</h5>

                {selectedFiles.map((file, index) => (
                  <div key={index} className="selected-file">
                    📄 {file.name}
                  </div>
                ))}
              </div>
            )}

            <button
              className="invite-btn"
              onClick={handleUpload}
              disabled={uploadLoading}
            >
              {uploadLoading ? "Uploading..." : "Upload Documents"}
            </button>
          </div>
        </div>
      )}

      <div className="chart-row">
        <div className="chart-card">
          <h3>Complaint Status</h3>

          <ResponsiveContainer width="100%" height={320}>
            <PieChart>
              <Pie
                data={complaintData}
                dataKey="value"
                nameKey="name"
                outerRadius={110}
                label
              >
                {complaintData.map((entry, index) => (
                  <Cell key={index} fill={COLORS[index]} />
                ))}
              </Pie>

              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Tasks Per User</h3>

          <ResponsiveContainer width="100%" height={320}>
            <BarChart
              layout="vertical"
              data={userTasks}
              margin={{
                top: 10,
                right: 20,
                left: 20,
                bottom: 10,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />

              <XAxis type="number" />

              <YAxis type="category" dataKey="assigned_to" />

              <Tooltip />

              <Bar dataKey="total_tasks" fill="#3E826D" radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="table-card">
        <h3>Tasks Per User</h3>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Total Tasks</th>
            </tr>
          </thead>

          <tbody>
            {userTasks.length > 0 ? (
              userTasks.map((user, index) => (
                <tr key={index}>
                  <td>{user.assigned_to}</td>
                  <td>{user.total_tasks}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2">No task data available.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-card">
        <h3>Top Questions</h3>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Asked</th>
            </tr>
          </thead>

          <tbody>
            {questionInsights.top_questions_asked.map((item, index) => (
              <tr key={index}>
                <td>{item.question}</td>
                <td>{item.times_asked}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-card">
        <h3>Lowest Relevance Scores</h3>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>Question</th>
              <th>Score</th>
            </tr>
          </thead>

          <tbody>
            {questionInsights.lowest_relevance_scores.map((item, index) => (
              <tr key={index}>
                <td>{item.question}</td>
                <td>{item.avg_relevance_score}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-card">
        <div className="table-header">
          <h3>Uploaded Files</h3>
          <span>{uploadedFiles.length} Files</span>
        </div>

        <table className="dashboard-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Filename</th>
            </tr>
          </thead>

          <tbody>
            {uploadedFiles.length > 0 ? (
              uploadedFiles.map((file, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{file.filename}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="2">No uploaded files found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
